import { useCallback, useEffect, useState } from 'react'

import { DropResult } from 'react-beautiful-dnd'

import { AiOperatorActionType } from '@/graphql/generated/schemas'

import { Criterion, CriterionOption } from '../types/types'

export const useCriteriaManager = (
  onChange?: (criteria: Criterion[]) => void
) => {
  const [criteria, setCriteria] = useState<Criterion[]>([])
  const [availableCriteria, setAvailableCriteria] = useState<CriterionOption[]>(
    []
  )
  const [backendOptions, setBackendOptions] = useState<CriterionOption[]>([])
  const [hasError, setHasError] = useState(false)

  const initializeCriteria = useCallback((initialCriteria: Criterion[]) => {
    setCriteria(initialCriteria)

    const options = initialCriteria.map((item) => item.criterion)
    setBackendOptions(options)
  }, [])

  useEffect(() => {
    const usedCriteriaValues = criteria.map((item) => item.criterion.value)

    const available = backendOptions.filter(
      (option) => !usedCriteriaValues.includes(option.value)
    )
    setAvailableCriteria(available)
    setHasError(criteria.length === 0)

    if (onChange) {
      onChange(criteria)
    }
  }, [criteria, backendOptions, onChange])

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      if (!result.destination) return

      const items = Array.from(criteria)
      const [reorderedItem] = items.splice(result.source.index, 1)
      items.splice(result.destination.index, 0, reorderedItem)

      setCriteria(items)
    },
    [criteria]
  )

  const handleDelete = useCallback(
    (id: string) => {
      const newCriteria = criteria.filter((item) => item.id !== id)
      setCriteria(newCriteria)
    },
    [criteria]
  )

  const handleAddCriterion = useCallback(() => {
    if (availableCriteria.length === 0) return

    const newCriterion: Criterion = {
      id: availableCriteria[0].value,
      action: AiOperatorActionType.ResolveAndMarkAsFalse,
      criterion: availableCriteria[0],
      detected: true,
    }

    setCriteria((prev) => [...prev, newCriterion])
  }, [availableCriteria])

  const handleUpdateCriterion = useCallback(
    (id: string, newCriterion: CriterionOption) => {
      setCriteria((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                id: newCriterion.value,
                criterion: newCriterion,
              }
            : item
        )
      )
    },
    []
  )

  return {
    criteria,
    availableCriteria,
    hasError,
    initializeCriteria,
    handleDragEnd,
    handleDelete,
    handleAddCriterion,
    handleUpdateCriterion,
  }
}
